import {
  InviteMemberRequest,
  InviteMemberResponse,
  MemberPermissions,
  OrgRole,
  PermissionCatalog,
  SeatUsage,
  TeamOverview,
} from "@/types/team"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import apiClient from "./apiClient"

const TEAM_QUERY_KEY = ["team"] as const
const SEATS_QUERY_KEY = ["teamSeats"] as const
const CATALOG_QUERY_KEY = ["teamPermissionCatalog"] as const
const memberPermsKey = (userSub: string) =>
  ["teamMemberPermissions", userSub] as const

const teamApiCalls = {
  getTeam: async (): Promise<TeamOverview> => {
    const response = await apiClient.get<TeamOverview>("/team")
    return response.data
  },
  getSeats: async (): Promise<SeatUsage> => {
    const response = await apiClient.get<SeatUsage>("/team/seats")
    return response.data
  },
  invite: async (body: InviteMemberRequest): Promise<InviteMemberResponse> => {
    const response = await apiClient.post<InviteMemberResponse>(
      "/team/invitations",
      body,
    )
    return response.data
  },
  resendInvite: async (invitationId: string): Promise<void> => {
    await apiClient.post(`/team/invitations/${invitationId}/resend`)
  },
  revokeInvite: async (invitationId: string): Promise<void> => {
    await apiClient.delete(`/team/invitations/${invitationId}`)
  },
  removeMember: async (userSub: string): Promise<void> => {
    await apiClient.delete(`/team/members/${userSub}`)
  },
  updateRole: async (params: {
    userSub: string
    role: OrgRole
  }): Promise<void> => {
    await apiClient.put(`/team/members/${params.userSub}/role`, {
      role: params.role,
    })
  },
  getPermissionCatalog: async (): Promise<PermissionCatalog> => {
    const response = await apiClient.get<PermissionCatalog>(
      "/team/permissions/catalog",
    )
    return response.data
  },
  getMemberPermissions: async (userSub: string): Promise<MemberPermissions> => {
    const response = await apiClient.get<MemberPermissions>(
      `/team/members/${userSub}/permissions`,
    )
    return response.data
  },
  updateMemberPermissions: async (params: {
    userSub: string
    permissions: string[]
  }): Promise<MemberPermissions> => {
    const response = await apiClient.put<MemberPermissions>(
      `/team/members/${params.userSub}/permissions`,
      { permissions: params.permissions },
    )
    return response.data
  },
}

export const useGetTeam = (enabled: boolean = true) =>
  useQuery({
    queryKey: TEAM_QUERY_KEY,
    queryFn: teamApiCalls.getTeam,
    enabled,
  })

export const useGetSeats = (enabled: boolean = true) =>
  useQuery({
    queryKey: SEATS_QUERY_KEY,
    queryFn: teamApiCalls.getSeats,
    enabled,
  })

const useTeamMutation = <TArgs, TResult>(
  mutationFn: (args: TArgs) => Promise<TResult>,
) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn,
    onSuccess: () => {
      // Team roster and seat usage both change on any of these operations.
      queryClient.invalidateQueries({ queryKey: TEAM_QUERY_KEY })
      queryClient.invalidateQueries({ queryKey: SEATS_QUERY_KEY })
    },
  })
}

export const useInviteMember = () => useTeamMutation(teamApiCalls.invite)
export const useResendInvite = () => useTeamMutation(teamApiCalls.resendInvite)
export const useRevokeInvite = () => useTeamMutation(teamApiCalls.revokeInvite)
export const useRemoveMember = () => useTeamMutation(teamApiCalls.removeMember)
export const useUpdateRole = () => useTeamMutation(teamApiCalls.updateRole)

// Catalog is org-wide and stable within a session.
export const useGetPermissionCatalog = (enabled: boolean = true) =>
  useQuery({
    queryKey: CATALOG_QUERY_KEY,
    queryFn: teamApiCalls.getPermissionCatalog,
    enabled,
    staleTime: 30 * 60 * 1000,
  })

export const useGetMemberPermissions = (
  userSub: string | null,
  enabled: boolean = true,
) =>
  useQuery({
    queryKey: memberPermsKey(userSub ?? ""),
    queryFn: () => teamApiCalls.getMemberPermissions(userSub as string),
    enabled: enabled && !!userSub,
  })

export const useUpdateMemberPermissions = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: teamApiCalls.updateMemberPermissions,
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: memberPermsKey(variables.userSub),
      })
      queryClient.invalidateQueries({ queryKey: TEAM_QUERY_KEY })
    },
  })
}
